import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { subscriptionStatusOf } from '@/lib/plan-distribution'
import { RevenueService, type PlanPriceAgency } from '@/services/revenue'

export interface PlanPriceTarget {
    planId: number
    /** null is the "Price not on record" row. */
    monthlyAmount: number | null
    /** e.g. "Solo · $99/mo" */
    title: string
}

const formatAmount = (value: number) =>
    `$${value.toLocaleString('en-US', {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
    })}`

/** The agencies behind one price row of the Plan Distribution card. */
export function PlanPriceAgenciesDialog({ target, onClose }: { target: PlanPriceTarget | null; onClose: () => void }) {
    const [agencies, setAgencies] = useState<PlanPriceAgency[] | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (!target) return
        let cancelled = false
        setAgencies(null)
        setFailed(false)
        RevenueService.getPlanPriceAgencies(target.planId, target.monthlyAmount)
            .then((rows) => {
                if (!cancelled) setAgencies(rows)
            })
            .catch(() => {
                if (!cancelled) setFailed(true)
            })
        return () => {
            cancelled = true
        }
    }, [target])

    const notOnRecord = target?.monthlyAmount === null
    const totalMrr = agencies?.reduce((sum, agency) => sum + agency.mrr, 0) ?? 0

    return (
        <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{target?.title}</DialogTitle>
                    <DialogDescription>
                        {agencies
                            ? `${agencies.length.toLocaleString('en-US')} ${agencies.length === 1 ? 'agency' : 'agencies'} · ${formatAmount(totalMrr)} MRR. Only active subscriptions count toward MRR.`
                            : 'Agencies billed on this price.'}
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="px-0 py-0">
                    {failed ? (
                        <p className="px-6 py-10 text-center text-sm text-muted-foreground">Couldn't load these agencies. Close and try again.</p>
                    ) : !agencies ? (
                        <div className="flex justify-center py-10">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : agencies.length === 0 ? (
                        <p className="px-6 py-10 text-center text-sm text-muted-foreground">No agencies on this price right now.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="pl-6">Agency</TableHead>
                                    <TableHead>Status</TableHead>
                                    {notOnRecord && <TableHead>Stripe price</TableHead>}
                                    <TableHead className="pr-6 text-right">MRR</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {agencies.map((agency) => {
                                    const status = subscriptionStatusOf(agency.stripeStatus)
                                    return (
                                        <TableRow key={agency.id}>
                                            <TableCell className="max-w-[16rem] pl-6">
                                                <Link to={`/agencies/${agency.id}`} onClick={onClose} className="block truncate font-medium hover:underline">
                                                    {agency.name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                <Badge tone={status.tone}>{status.label}</Badge>
                                            </TableCell>
                                            {notOnRecord && (
                                                <TableCell className="max-w-[12rem] truncate font-mono text-xs text-muted-foreground">
                                                    {agency.stripePriceId ?? 'None'}
                                                </TableCell>
                                            )}
                                            <TableCell className={agency.mrr > 0 ? 'pr-6 text-right tabular-nums' : 'pr-6 text-right tabular-nums text-muted-foreground'}>
                                                {formatAmount(agency.mrr)}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    )}
                </DialogBody>
            </DialogContent>
        </Dialog>
    )
}
